import React from 'react';
import CaptureJourneyGraphStack from './CaptureJourneyGraphStack';
import ProductMockup from './ProductMockup';
import { AnimatedNumber, useRevealInView, useScrollParallaxVar } from '../lib/landingMotion';

const journeySignals = [
    'Track performance and physique in one place',
    'Body journal with photos and videos',
    'See visual and numerical progress side by side',
];

const measurementSparkline = '6,12 20,18 34,14 48,18 62,16 76,24 92,27 108,24 118,25';

function Sparkline({ points, className = '' }) {
    return (
        <svg className={`capture-sparkline ${className}`.trim()} viewBox="0 0 120 40" preserveAspectRatio="none" aria-hidden="true">
            <polyline points={points} />
        </svg>
    );
}

export default function AboutSection() {
    const captureMotion = useRevealInView({ threshold: 0.16, rootMargin: '0px 0px -8% 0px' });
    const progressMotion = useRevealInView({ threshold: 0.18, rootMargin: '0px 0px -8% 0px' });
    const detailParallaxRef = useScrollParallaxVar({
        property: '--parallax-y',
        distance: 24,
        reducedMotion: progressMotion.prefersReducedMotion,
    });
    const overviewParallaxRef = useScrollParallaxVar({
        property: '--parallax-y',
        distance: 15,
        reducedMotion: progressMotion.prefersReducedMotion,
    });

    return (
        <>
            <section
                ref={captureMotion.ref}
                className={`landing-section landing-capture ${captureMotion.isVisible ? 'is-visible' : ''}`}
                id="capture-journey"
                data-reveal-root
            >
                <div className="site-container landing-capture__grid">
                    <div className="landing-capture__copy">
                        <p className="landing-kicker" data-reveal-item style={{ '--delay': '0ms' }}>
                            Data & visuals
                        </p>

                        <h2 className="landing-display" data-reveal-item style={{ '--delay': '70ms' }}>
                            <span>Capture your</span>
                            <span>Journey</span>
                        </h2>

                        <ul className="landing-bullets" data-reveal-item style={{ '--delay': '150ms' }}>
                            {journeySignals.map((signal) => (
                                <li key={signal}>
                                    <span className="landing-bullets__dot" aria-hidden="true" />
                                    <span>{signal}</span>
                                </li>
                            ))}
                        </ul>

                        <CaptureJourneyGraphStack />
                    </div>

                    <div className="landing-capture__visual">
                        <div className="landing-capture__phone-wrap" data-reveal-item style={{ '--delay': '100ms' }}>
                            <ProductMockup
                                asset="bodyJournal"
                                alt="TECNIQ body journal preview"
                                objectFit="contain"
                            />
                        </div>

                        <article className="measurement-card" data-reveal-item style={{ '--delay': '180ms' }}>
                            <div className="measurement-card__header">
                                <h3 className="measurement-card__title">Body Fat</h3>
                                <span className="measurement-card__badge">-5%</span>
                            </div>

                            <div className="measurement-card__chart">
                                <Sparkline className="app-sparkline" points={measurementSparkline} />
                            </div>

                            <div className="measurement-card__footer">
                                <span className="measurement-card__timestamp">22d ago</span>
                                <strong className="measurement-card__value">
                                    <AnimatedNumber
                                        target={12.5}
                                        decimals={1}
                                        suffix="%"
                                        duration={450}
                                        delay={90}
                                        trigger={captureMotion.isVisible}
                                        reducedMotion={captureMotion.prefersReducedMotion}
                                    />
                                </strong>
                            </div>
                        </article>

                        <div className="video-preview" data-reveal-item style={{ '--delay': '260ms' }}>
                            <ProductMockup
                                asset="videoPreviewCard"
                                className="video-preview__image"
                                alt="TECNIQ media progress preview"
                                objectFit="contain"
                            />
                        </div>
                    </div>
                </div>
            </section>

            <section
                ref={progressMotion.ref}
                className={`landing-section landing-progress ${progressMotion.isVisible ? 'is-visible' : ''}`}
                id="built-for-progress"
                data-reveal-root
            >
                <div className="site-container landing-progress__grid">
                    <article className="progress-showcase progress-showcase--analysis">
                        <div className="landing-copy">
                            <p className="landing-kicker" data-reveal-item style={{ '--delay': '0ms' }}>
                                Built for progress
                            </p>
                            <h2 className="progress-showcase__title" data-reveal-item style={{ '--delay': '70ms' }}>
                                <span>Analyse. Improve.</span>
                            </h2>
                        </div>

                        <div className="progress-showcase__visual progress-showcase__visual--analysis" data-reveal-item style={{ '--delay': '150ms' }}>
                            <div ref={detailParallaxRef} className="progress-showcase__parallax">
                                <ProductMockup
                                    asset="analyticsDetail"
                                    className="progress-showcase__image"
                                    alt="TECNIQ analytics detail preview"
                                    objectFit="contain"
                                />
                            </div>
                        </div>
                    </article>

                    <article className="progress-showcase progress-showcase--goals">
                        <div className="progress-showcase__visual progress-showcase__visual--goals" data-reveal-item style={{ '--delay': '120ms' }}>
                            <div ref={overviewParallaxRef} className="progress-showcase__parallax">
                                <ProductMockup
                                    asset="analyticsOverview"
                                    className="progress-showcase__image"
                                    alt="TECNIQ analytics overview preview"
                                    objectFit="contain"
                                    objectPosition="center top"
                                />
                            </div>
                        </div>

                        <div className="landing-copy">
                            <p className="landing-kicker" data-reveal-item style={{ '--delay': '200ms' }}>
                                Built for progress
                            </p>
                            <h2 className="progress-showcase__title" data-reveal-item style={{ '--delay': '260ms' }}>
                                <span>Define. Track.</span>
                                <span>Achieve</span>
                            </h2>
                        </div>
                    </article>
                </div>
            </section>
        </>
    );
}
